import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Linkedin, MapPin, ArrowUpRight, CheckCircle2, AlertCircle, MessageCircle, Phone } from "lucide-react";
import { TID } from "../constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Use same-origin /api so the form works on Emergent (ingress routes /api → backend)
// AND on Cloudflare Pages (Pages Function at /functions/api/contact.js handles it).
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const CONTACT_EMAIL = "stoicescualflorin@labourengine.com";
const CONTACT_PHONE_TEL = "+40725318017"; // tel: links must omit spaces
const CONTACT_WHATSAPP = "40725318017"; // wa.me requires no plus, no spaces

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export function Contact({ t }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !isValidEmail(form.email) || !form.message.trim()) {
      setStatus({ state: "error", message: t.contact.form.invalid });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setStatus({ state: "success", message: t.contact.form.success });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", message: t.contact.form.error });
    }
  };

  return (
    <section id="contact" className="relative section-pad border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="md:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-[#00E5FF]" />
              <span className="eyebrow">{t.contact.eyebrow}</span>
            </div>
            <h2
              className="text-white font-semibold leading-[1.05] tracking-tight"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "clamp(30px, 4.5vw, 52px)",
              }}
            >
              {t.contact.heading}
            </h2>
            <p
              className="mt-6 text-[#9BA1A6] leading-relaxed max-w-md"
              style={{ fontFamily: "'Manrope', sans-serif", fontSize: "16px" }}
            >
              {t.contact.body}
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid={TID.contactEmailBtn}
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#00E5FF]/60 bg-[#1C2331] p-5 rounded-sm transition-all duration-300"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0B132B] border border-white/10 rounded-sm">
                    <Mail size={16} className="text-[#00E5FF]" />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.email_label}
                    </div>
                    <div
                      className="text-white text-sm font-semibold truncate"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {CONTACT_EMAIL}
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-white shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <a
                href={`https://wa.me/${CONTACT_WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#00E5FF]/60 bg-[#1C2331] p-5 rounded-sm transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0B132B] border border-white/10 rounded-sm">
                    <MessageCircle size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.whatsapp_label}
                    </div>
                    <div
                      className="text-white text-sm font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.whatsapp_value}
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                data-testid="contact-phone-button"
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#00E5FF]/60 bg-[#1C2331] p-5 rounded-sm transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0B132B] border border-white/10 rounded-sm">
                    <Phone size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.phone_label}
                    </div>
                    <div
                      className="text-white text-sm font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.phone_value}
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <a
                href="https://www.linkedin.com/in/florin-alexandru-stoicescu-447731133"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TID.contactLinkedinBtn}
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#00E5FF]/60 bg-[#1C2331] p-5 rounded-sm transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0B132B] border border-white/10 rounded-sm">
                    <Linkedin size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t.contact.linkedin_label}
                    </div>
                    <div
                      className="text-white text-sm font-semibold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Connect on LinkedIn
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <div className="flex items-center gap-3 pt-4 text-[#9BA1A6]">
                <MapPin size={14} className="text-[#00E5FF]" />
                <span
                  className="text-[12px] uppercase tracking-[0.22em] font-semibold"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {t.contact.location}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            noValidate
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            className="md:col-span-7 bg-[#1C2331] border border-white/10 rounded-sm p-8 md:p-10"
          >
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {t.contact.form.name}
              </label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={handle("name")}
                data-testid={TID.contactFormName}
                className="le-input w-full"
                placeholder="John Doe"
                autoComplete="name"
              />
            </div>

            <div className="space-y-2 mt-8">
              <label
                htmlFor="email"
                className="block text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {t.contact.form.email}
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={handle("email")}
                data-testid={TID.contactFormEmail}
                className="le-input w-full"
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>

            <div className="space-y-2 mt-8">
              <label
                htmlFor="message"
                className="block text-[10px] uppercase tracking-[0.24em] text-[#9BA1A6] font-semibold"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {t.contact.form.message}
              </label>
              <textarea
                id="message"
                rows={5}
                value={form.message}
                onChange={handle("message")}
                data-testid={TID.contactFormMessage}
                className="le-input w-full resize-none"
                placeholder="Tell me about the process you want to automate…"
              />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid={TID.contactFormSubmit}
                className="inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00C2D6] disabled:opacity-60 disabled:cursor-not-allowed text-[#0B132B] font-bold px-8 py-4 rounded-sm transition-all duration-300"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  letterSpacing: "0.06em",
                  boxShadow: "0 0 25px rgba(0,229,255,0.18)",
                }}
              >
                {status.state === "loading" ? t.contact.form.sending : t.contact.form.submit}
                <ArrowUpRight size={16} />
              </button>

              {status.state === "success" && (
                <div
                  data-testid={TID.contactFormFeedback}
                  className="flex items-center gap-2 text-[#00E5FF] text-sm"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <CheckCircle2 size={16} />
                  {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid={TID.contactFormFeedback}
                  className="flex items-center gap-2 text-red-400 text-sm"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <AlertCircle size={16} />
                  {status.message}
                </div>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
